(ns shared.core.components.responsive
  (:require [shared.core.reactive :as re]
            [shared.core.util :as util]))

(def pred-key-cutoffs {:sm 640
                       :md 768
                       :lg 1024
                       :xl 1280
                       :2xl 1536})

(defn at-or-below-width [w-check]
  (fn [_ w] (<= w w-check)))

(defn inject-at-position [items {:keys [item position]}]
  (util/insert-at-position items item position))

(defn build-resp-props [args {:keys [height width]}]
  (let [custom-pred-positions (->> (:resp args)
                                   (map-indexed (fn [idx i]
                                                  (when-not (keyword? (first i))
                                                    {:item i
                                                     :position idx})))
                                   (filter identity))
        sorted-resp (->> (:resp args)
                         (filter (fn [[pred _]] (keyword? pred)))
                         (sort-by #(get pred-key-cutoffs (first %)))
                         (reverse)
                         (#(reduce inject-at-position % custom-pred-positions)))]
    (some->> sorted-resp
             (filter (fn [[pred _]]
                       (let [pred (condp = pred
                                    :sm (at-or-below-width 640)
                                    :md (at-or-below-width 768)
                                    :lg (at-or-below-width 1024)
                                    :xl (at-or-below-width 1280)
                                    :2xl (at-or-below-width 1536)
                                    pred)]
                         (pred height width))))
             (map second))))

(defn prop-keys
  "Collects all prop keys from a collection of prop maps"
  [props]
  (->> props
       (map #(map first %))
       (flatten)
       (distinct)))

(defn reduce-props
  "Reduces each prop to a single value"
  [base-args resp-props]
  (some->> resp-props
           (prop-keys)
           (map (fn [k]
                  (reduce (fn [r v]
                            (let [r-v (get r k)
                                  k-v (get v k)]
                              (cond
                                (map? k-v) (hash-map k (merge r-v k-v))
                                k-v (hash-map k k-v)
                                :else r)))
                          (get base-args k)
                          resp-props)))
           (apply merge)))

(defn merge-with-base [base to-merge]
  (let [base-keys (keys base)]
    (->> base-keys
         (map (fn [k]
                (let [merge? (map? (get base k))]
                  (hash-map k (if merge?
                                (merge (get base k) (get to-merge k))
                                (or (get to-merge k) (get base k)))))))
         (apply merge)
         (merge (apply dissoc base base-keys) (apply dissoc to-merge base-keys)))))

(defn merge-resp-props
  "Merge in props to general prop value based on `resp-props` that satisfy current viewport size checks"
  [args resp-props]
  (-> args
      (dissoc :resp)
      (merge-with-base (reduce-props args resp-props))))

(defn hide-cmp?
  "Support fully hiding / showing components at breakpoints"
  [resp-props]
  (some->> resp-props (last) :hide?))

(defn resp
  "Component for conditionally adding responsive styling or hiding / showing component at breakpoints. You can send the
   typical `style` property that are your base styles, but then you can also pass a property called `resp`. This is a
   map of breakpoints (represented as known keywords or a more flexible function that returns `true` or `false` based on
   the provided height and width values for the viewport). Based on the `resp` keys that evaluate to `true`, the
   associated properties are used. For the case of styles, these all get merged in (from top to bottom). There is also
   support for a `hide?` property. This value is only used for the last entry in the map that evaluates to `true`. If
   `true`, the component is not rendered. This makes it easy to hide / show a component at certain breakpoints. Please
   note that the `cmp` arg can be a reagent component or a hiccup keyword, like `:div`.

   Ex:
   [c/resp c/a {:style {:color \"orange\"}
                :resp {(fn [_ w] (<= w 1100)) {:style {:color \"black\"}}
                       :lg {:style {:color \"green\"}
                            :hide? true}
                       (fn [_ w] (<= w 1000)) {:style {:color \"blue\"}}
                       :md {:style {:color \"purple\"}}
                       :sm {:style {:color \"pink\"}}
                       :2xl {:style {:color \"red\"}}}}
    \"Some Link\"]"
  [_ _ _]
  (let [vs- (re/subscribe [:core/viewport-size])]
    (fn [cmp args-or-nested-cmp & contents]
      (let [map-args (if (map? args-or-nested-cmp)
                       args-or-nested-cmp
                       (some->> contents
                                (filter map?)
                                (first)))]
        (when-not (some-> map-args (build-resp-props @vs-) (hide-cmp?))
          (if (or (not args-or-nested-cmp) (map? args-or-nested-cmp))
            (let [resp-props (build-resp-props args-or-nested-cmp @vs-)]
              (into
                [cmp (merge-resp-props args-or-nested-cmp resp-props)]
                (remove map? contents)))
            [cmp
             (into [resp args-or-nested-cmp] contents)]))))))
